import { createContext, ReactNode, useContext } from 'react';
import { Navigate, RouteObject, useLocation } from 'react-router-dom';
import { Routes } from '../enums/Routes.ts';
import { LoginPage } from '../pages/LoginPage/LoginPage.tsx';
import { SignUpPage } from '../pages/SignUp';
import { LegalDocumentPage } from '../pages/LegalDocument/LegalDocumentPage';
import { GoalPage, RolePage } from '../pages/GettingStarted/ChoicePages';
import { FormSubmission } from '../pages/FormSubmission';
import { WorkEnvironmentFormSubmission } from '../pages/FormSubmission/WorkEnvironmentFormSubmission';
import { CustomSurveyFormSubmission } from '../pages/CustomSurveyFormSubmission/CustomSurveyFormSubmission';
import { VerifyAccount } from '../pages/VerifyAccount';
import { AppShellRouter } from '../components/AppShellRouter/AppShellRouter';
import { DashboardPage } from '../layouts/TeamDashboardPageLayout';
import { HRDashboard } from '../pages/HRDashboard';
import { HRHome } from '../pages/HRDashboard';
import { HRWorkEnvironment } from '../pages/HRDashboard';
import { Teams } from '../pages/Teams';
import { Team } from '../pages/Team';
import { SurveysPage } from '../pages/SurveysPage';
import { AnalyzeSurvey } from '../pages/AnalyzeSurvey';
import { SettingsPage } from '../pages/Settings/SettingsPage';
import { TeamAccessManagement } from '../pages/TeamAccessMangement';
import { TeamIntegration } from '../pages/TeamIntegration/TeamIntegration.tsx';
import { TeamSchedulingPage } from '../pages/Teams/TeamScheduling/TeamSchedulingPage';
import { OrganizationSchedulingPage } from '../pages/Organizations/Scheduling/OrganizationSchedulingPage';
import { OrganizationGeneralPage } from '../pages/Organizations/General/OrganizationGeneralPage';
import { OrganizationSettingsPage } from '../pages/Organizations/Settings/OrganizationSettingsPage';
import { OrganizationBusinessPage } from '../pages/Organizations/Business/OrganizationBusinessPage';
import { ForgotPasswordPage } from '../pages/ForgotPasswordPage/ForgotPasswordPage.tsx';
import { ExpiredTrialPage } from '../pages/ExpiredTrialPage/ExpiredTrialPage.tsx';
import { TeamsOverviewPage } from '../pages/Organizations/Teams/TeamsOverviewPage.tsx';
import { CreateTeamPage } from '../pages/Organizations/Teams/CreateTeamPage/CreateTeamPage.tsx';
import { UsersOverviewPage } from '../pages/Organizations/Users/UsersOverviewPage.tsx';
import { OrganizationUsagePage } from '../pages/Organizations/Usage/OrganizationUsagePage.tsx';
import { TeamUsageDetailsPage } from '../pages/Organizations/Usage/TeamUsageDetailsPage.tsx';
import { InviteUserPage } from '../pages/Organizations/Users/InviteUserPage';
import { ViewUserPage } from '../pages/Organizations/Users/ViewUserPage';
import { EditUserPage } from '../pages/Organizations/Users/EditUserPage';
import { SubscriptionsPage } from '../pages/Organizations/Subscriptions/SubscriptionsPage';
import { AnalyzationSummary } from '../components/AnalyzationSummary/AnalyzationSummary.tsx';
import { ContentSystemArticlesPage } from '../pages/ContentSystemArticlesPage';
import { ContentSystemPage } from '../pages/ContentSystemPage';
import { ContentSystemPageLayout } from '../layouts/ContentSystemPageLayout';
import { ArticlePage } from '../pages/ArticlePage/ArticlePage.tsx';
import { QuestionTrends } from '../pages/QuestionTrends';
import { TeamDashboard } from '../pages/TeamDashboard';
import { WorkEnvironmentDashboardPage } from '../pages/WorkEnvironmentDashboard';
import { CustomSurveyDashboardPage } from '../pages/CustomSurveyDashboard/CustomSurveyDashboardPage';
import { WorkEnvironmentSurveyDetailPage } from '../pages/WorkEnvironmentSurveyDetail/WorkEnvironmentSurveyDetailPage';
import { WorkEnvironmentTeamMeasures } from '../pages/WorkEnvironmentTeamMeasures/WorkEnvironmentTeamMeasures';
import { WorkEnvironmentTeamPage } from '../pages/WorkEnvironmentTeamPage/WorkEnvironmentTeamPage';
import { PublicTeamHealthMetrics } from '../pages/PublicTeamHealthMetrics/PublicTeamHealthMetrics';
import { TeamsOAuthCallbackPage } from '../pages/TeamsOAuthCallback/TeamsOAuthCallbackPage';
import { SlackOAuthCallbackPage } from '../pages/SlackOAuthCallback/SlackOAuthCallbackPage';
import { CreateSurveyPage } from '../pages/CreateSurveyPage/CreateSurveyPage.tsx';
import { EditPlannedSurveyPage } from '../pages/EditPlannedSurveyPage/EditPlannedSurveyPage.tsx';
import { HRPsychologicalSafety } from '../pages/HRDashboard/HRPsychologicalSafety';
import { HRRiskMatrix } from '../pages/HRDashboard/HRRiskMatrix';
import { HRRiskFactors } from '../pages/HRDashboard/HRRiskFactors';
import { ChecklistsPage } from '../pages/SafetyRounds/Checklists/ChecklistsPage';
import { PlansPage } from '../pages/SafetyRounds/Plans/PlansPage';
import { PlanFormPage } from '../pages/SafetyRounds/Plans/PlanFormPage';
import { PlanChecklistPage } from '../pages/SafetyRounds/Plans/PlanChecklistPage';
import { RoundPage } from '../pages/SafetyRounds/Round/RoundPage';
import { SafetyRepArea } from '../components/SafetyRepArea/SafetyRepArea';
import { ProtocolPage } from '../pages/SafetyRounds/Protocol/ProtocolPage';
import { DeviationPage } from '../pages/SafetyRounds/Deviations/DeviationPage';
import { MyDeviationsPage } from '../pages/SafetyRounds/Deviations/MyDeviationsPage';
import { OversightPage } from '../pages/SafetyRounds/Oversight/OversightPage';
import { LogPdfPage } from '../pages/SafetyRounds/Oversight/LogPdfPage';
import { GeneralManagerTasksPage } from '../pages/SafetyRounds/Tasks/GeneralManagerTasksPage';
import { NotificationSettingsPage } from '../pages/SafetyRounds/Notifications/NotificationSettingsPage';
import { NotificationCenterPage } from '../pages/NotificationCenter/NotificationCenterPage';
import { ChecklistEditorPage } from '../pages/SafetyRounds/Checklists/ChecklistEditorPage';
import { HRSafetyReps } from '../pages/HRDashboard/HRSafetyReps';
import { HRMeasureDeadlines } from '../pages/HRDashboard/HRWorkEnvironment/MeasureDeadlines/HRMeasureDeadlines';
import { HRMissingSafetyReps } from '../pages/HRDashboard/HRWorkEnvironment/MissingSafetyReps/HRMissingSafetyReps';
import { HRSurveysDue } from '../pages/HRDashboard/HRWorkEnvironment/SurveysDue/HRSurveysDue';
import { HRRequirements } from '../pages/HRDashboard/HRWorkEnvironment/Requirements/HRRequirements';
import { HRProcess } from '../pages/HRDashboard/HRWorkEnvironment/Process/HRProcess';
import { HRRisks } from '../pages/HRDashboard/HRWorkEnvironment/Risks/HRRisks';
import { HRTeams } from '../pages/HRDashboard/HRWorkEnvironment/Teams/HRTeams';
import {
  SafetyRepCompleted,
  SafetyRepOverview,
  SafetyRepTasks,
  SafetyRepTeams,
  SafetyRepVersion,
} from '../pages/SafetyRep';
import { SystemAdminLayout } from '../components/SystemAdminLayout';
import { SystemAdminDashboard } from '../pages/SystemAdmin/SystemAdminDashboard/SystemAdminDashboard';
import { TenantOverviewPage } from '../pages/SystemAdmin/TenantOverviewPage';
import { CreateTenantPage } from '../pages/SystemAdmin/CreateTenantPage';
import { TenantDetailPageNew } from '../pages/SystemAdmin/TenantDetailPage/TenantDetailPageNew';
import { TenantFeaturesPage } from '../pages/SystemAdmin/TenantFeaturesPage/TenantFeaturesPage';
import { EditSmsQuotaPage } from '../pages/SystemAdmin/EditSmsQuotaPage';
import { EditSeatsPage } from '../pages/SystemAdmin/EditSeatsPage';
import { UpgradeTenantToPartnerPage } from '../pages/SystemAdmin/UpgradeTenantToPartnerPage';
import { PartnerOverviewPage } from '../pages/SystemAdmin/PartnerOverviewPage/PartnerOverviewPage';
import { CreatePartnerPage } from '../pages/SystemAdmin/CreatePartnerPage/CreatePartnerPage';
import { PartnerDetailPage } from '../pages/SystemAdmin/PartnerDetailPage/PartnerDetailPage';
import { EditPartnerPage } from '../pages/SystemAdmin/EditPartnerPage';
import { PartnerLeadsOverviewPage } from '../pages/SystemAdmin/PartnerLeadsOverviewPage';
import { UserDetailPage } from '../pages/SystemAdmin/UserDetailPage/UserDetailPage';
import { PartnerAdminLayout } from '../components/PartnerAdminLayout';
import { PartnerAdminDashboard } from '../pages/PartnerAdmin/PartnerAdminDashboard/PartnerAdminDashboard';
import { CustomerListPage } from '../pages/PartnerAdmin/CustomerListPage/CustomerListPage';
import { CustomerDetailPage } from '../pages/PartnerAdmin/CustomerDetailPage/CustomerDetailPage';
import { CreateCustomerPage } from '../pages/PartnerAdmin/CreateCustomerPage/CreateCustomerPage';
import { CashbackPage } from '../pages/PartnerAdmin/CashbackPage/CashbackPage';
import { LeadListPage } from '../pages/PartnerAdmin/LeadListPage/LeadListPage';
import { CreateLeadPage } from '../pages/PartnerAdmin/CreateLeadPage/CreateLeadPage';
import { LeadDetailPage } from '../pages/PartnerAdmin/LeadDetailPage/LeadDetailPage';
import { EditLeadPage } from '../pages/PartnerAdmin/EditLeadPage/EditLeadPage';
import { CustomSurveyTemplatesPage } from '../pages/CustomSurveyTemplatesPage/CustomSurveyTemplatesPage';
import { CreateCustomSurveyTemplatePage } from '../pages/CreateCustomSurveyTemplatePage/CreateCustomSurveyTemplatePage';
import { CustomSurveyTemplateDetailPage } from '../pages/CustomSurveyTemplateDetailPage/CustomSurveyTemplateDetailPage';
import { CustomSurveyTemplateEditPage } from '../pages/CustomSurveyTemplateEditPage/CustomSurveyTemplateEditPage';
import { CustomSurveyResultsPage } from '../pages/CustomSurveyResultsPage/CustomSurveyResultsPage';
import { CustomSurveyTemplateTrendsPage } from '../pages/CustomSurveyTemplateTrends/CustomSurveyTemplateTrendsPage';
import { InterpretiveReportPage } from '../pages/InterpretiveReport/InterpretiveReportPage';
import { WorkEnvironmentReportPage } from '../pages/WorkEnvironmentReport/WorkEnvironmentReportPage';
import { WorkEnvironmentInspectionReportPage } from '../pages/WorkEnvironmentInspectionReport/WorkEnvironmentInspectionReportPage';
import { NotFoundPage, RequireRole, RequireTeamLeader } from '../components/PageProblem';
import { UserRoles } from '../api/queries';

// The parts of the app for one role. Others are told they have no access, and never see the part's menus or forms.
const hrOnly = (page: ReactNode) => <RequireRole anyOf={[UserRoles.HR_ADMIN]}>{page}</RequireRole>;
const orgAdminOnly = (page: ReactNode) => <RequireRole anyOf={[UserRoles.ORG_ADMIN]}>{page}</RequireRole>;
// The team leader's part: for those who lead a team, and the administrator. HR sees the teams from the HR dashboard.
const teamLeaderOnly = (page: ReactNode) => <RequireTeamLeader>{page}</RequireTeamLeader>;

const LOGGED_IN_ROUTES_ID = 'LOGGED_IN_ROUTES_ID';

type RouterMetadataContextProps = {
  shouldShowTeamSelector?: boolean;
  /** The page shows the team selector in its own header instead of the app shell */
  isTeamSelectorInPage?: boolean;
  hasBreadcrumbsInPage?: boolean;
  shouldBeInFocusMode?: boolean;
};

export type RouterMetadataProps = RouterMetadataContextProps & {
  children?: ReactNode;
};

const RouterMetadataContext = createContext<RouterMetadataContextProps>({
  shouldShowTeamSelector: false,
  isTeamSelectorInPage: false,
  hasBreadcrumbsInPage: false,
  shouldBeInFocusMode: false,
});

export const RouterMetadataProvider = ({ children }: RouterMetadataProps) => {
  const location = useLocation();

  const shouldShowTeamSelector = getTeamSelectorVisibility(location);
  const isTeamSelectorInPage = getTeamSelectorInPage(location);
  const hasBreadcrumbsInPage = matchRoute(
    location.pathname,
    collectRoutes((route) => !!route.breadcrumbsInPage),
  );
  const shouldBeInFocusMode = getShouldBeFocusMode(location);

  return (
    <RouterMetadataContext.Provider
      value={{
        shouldShowTeamSelector,
        isTeamSelectorInPage,
        hasBreadcrumbsInPage,
        shouldBeInFocusMode,
      }}
    >
      {children}
    </RouterMetadataContext.Provider>
  );
};

export const useRouterMetadataContext = function (): RouterMetadataProps {
  const context = useContext(RouterMetadataContext);
  if (context === undefined) {
    throw new Error(
      'useRouterMetadataContext must be used within a RouterMetadataContextProvider.',
    );
  }
  return context;
};

type ExtendedRouteObject = RouteObject & {
  showTeamSelector?: boolean;
  /** The page renders the team selector in its own header */
  teamSelectorInPage?: boolean;
  /** The page shows its own breadcrumbs, so the app shell leaves them out */
  breadcrumbsInPage?: boolean;
  focusMode?: boolean;
  customId?: string;
  hideRoute?: boolean;
};

type ExtendedRouterConfig = Array<
  ExtendedRouteObject & {
    children?: Array<ExtendedRouteObject>;
  }
>;

export const routesWithOptions: ExtendedRouterConfig = [
  {
    path: Routes.Root,
    element: <LoginPage />, // No sidebar for the root path
  },
  {
    path: Routes.ForgotPassword,
    element: <ForgotPasswordPage />, // No sidebar for the root path
  },
  {
    path: Routes.ExpiredTrial,
    element: <ExpiredTrialPage />,
  },
  {
    path: Routes.SignUp,
    element: <SignUpPage />,
  },
  {
    path: Routes.Terms,
    element: <LegalDocumentPage kind="TERMS" />,
  },
  {
    path: Routes.OnboardingRole,
    element: <RolePage />,
  },
  {
    path: Routes.OnboardingGoal,
    element: <GoalPage />,
  },
  {
    path: Routes.DataProcessingAgreement,
    element: <LegalDocumentPage kind="DPA" />,
  },
  {
    path: Routes.FormSubmission,
    element: <FormSubmission />,
  },
  {
    path: Routes.WorkEnvironmentFormSubmission,
    element: <WorkEnvironmentFormSubmission />,
  },
  {
    path: Routes.CustomSurveyFormSubmission,
    element: <CustomSurveyFormSubmission />,
  },
  {
    path: Routes.WorkEnvironmentActionProposal,
    element: <WorkEnvironmentTeamMeasures />,
  },
  {
    path: Routes.WorkEnvironmentActionVoting,
    element: <WorkEnvironmentTeamMeasures />,
  },
  {
    path: Routes.WorkEnvironmentTeamPage,
    element: <WorkEnvironmentTeamPage />,
  },
  {
    path: Routes.PublicTeamHealthMetrics,
    element: <PublicTeamHealthMetrics />,
  },
  {
    path: Routes.PublicActionPlanDirect,
    element: <PublicTeamHealthMetrics />,
  },
  {
    path: Routes.Verify,
    element: <VerifyAccount />,
  },
  {
    path: '/',
    customId: LOGGED_IN_ROUTES_ID,
    element: <AppShellRouter />,
    children: [
      // HR-routes
      {
        path: Routes.HRDashboard,
        element: hrOnly(<HRDashboard />),
        children: [
          {
            index: true,
            element: <Navigate to={Routes.HRHome} replace />,
          },
          {
            path: Routes.HRHome,
            element: <HRHome />,
          },
          {
            path: Routes.HRWorkEnvironment,
            element: <HRWorkEnvironment />,
          },
          {
            path: Routes.HRWorkEnvironmentMeasures,
            element: <HRMeasureDeadlines />,
          },
          {
            path: Routes.HRWorkEnvironmentMissingSafetyReps,
            element: <HRMissingSafetyReps />,
          },
          {
            path: Routes.HRWorkEnvironmentSurveysDue,
            element: <HRSurveysDue />,
          },
          {
            path: Routes.HRWorkEnvironmentRequirements,
            element: <HRRequirements />,
          },
          {
            path: Routes.HRWorkEnvironmentProcess,
            element: <HRProcess />,
          },
          {
            path: Routes.HRWorkEnvironmentRisks,
            element: <HRRisks />,
          },
          {
            path: Routes.HRWorkEnvironmentTeams,
            element: <HRTeams />,
          },
          {
            path: Routes.HRWorkEnvironmentSurveyDetail,
            element: <WorkEnvironmentSurveyDetailPage />,
          },
          {
            path: Routes.HRPsychologicalSafety,
            element: <HRPsychologicalSafety />,
          },
          {
            path: Routes.HRSafetyReps,
            element: <HRSafetyReps />,
          },
          {
            path: Routes.HRRiskMatrix,
            element: <HRRiskMatrix />,
          },
          {
            path: Routes.HRRiskFactors,
            element: <HRRiskFactors />,
          },
          {
            path: Routes.HRSafetyRoundOverview,
            element: <OversightPage />,
          },
          {
            path: Routes.HRSafetyRoundChecklists,
            element: <ChecklistsPage />,
          },
          {
            path: Routes.HRSafetyRoundChecklist,
            element: <ChecklistEditorPage />,
          },
        ],
      },
      // Safety representative routes
      {
        path: Routes.SafetyRepRoundPlans,
        element: <SafetyRepArea><PlansPage /></SafetyRepArea>,
      },
      {
        path: Routes.SafetyRepRoundPlanNew,
        element: <SafetyRepArea><PlanFormPage /></SafetyRepArea>,
      },
      {
        path: Routes.SafetyRepRoundPlan,
        element: <SafetyRepArea><PlanFormPage /></SafetyRepArea>,
      },
      {
        path: Routes.SafetyRepRoundPlanChecklist,
        element: <SafetyRepArea><PlanChecklistPage /></SafetyRepArea>,
      },
      {
        path: Routes.SafetyRepRound,
        element: <SafetyRepArea><RoundPage /></SafetyRepArea>,
      },
      {
        path: Routes.SafetyRepRoundProtocol,
        element: <SafetyRepArea><ProtocolPage /></SafetyRepArea>,
      },
      {
        path: Routes.Deviation,
        element: <DeviationPage />,
      },
      {
        path: Routes.MyDeviations,
        element: <MyDeviationsPage />,
      },
      {
        path: Routes.Notifications,
        element: <NotificationCenterPage />,
      },
      {
        path: Routes.GeneralManagerTasks,
        element: <GeneralManagerTasksPage />,
      },
      {
        path: Routes.GeneralManagerPlan,
        element: <PlanFormPage />,
      },
      {
        path: Routes.GeneralManagerRound,
        element: <RoundPage />,
      },
      {
        path: Routes.GeneralManagerProtocol,
        element: <ProtocolPage />,
      },
      {
        path: Routes.SafetyRoundNotifications,
        element: <SafetyRepArea><NotificationSettingsPage role="safetyRep" /></SafetyRepArea>,
      },
      {
        path: Routes.GeneralManagerNotifications,
        element: <NotificationSettingsPage role="generalManager" />,
      },
      {
        path: Routes.SafetyRoundOverview,
        element: <SafetyRepArea><OversightPage /></SafetyRepArea>,
      },
      {
        path: Routes.GeneralManagerSafetyRoundOverview,
        element: <OversightPage />,
      },
      {
        path: Routes.SafetyRoundLogPdf,
        element: <SafetyRepArea><LogPdfPage /></SafetyRepArea>,
      },
      {
        path: Routes.SafetyRepOverview,
        element: <SafetyRepArea><SafetyRepOverview /></SafetyRepArea>,
      },
      {
        path: Routes.SafetyRepTeams,
        element: <SafetyRepArea><SafetyRepTeams /></SafetyRepArea>,
      },
      {
        path: Routes.SafetyRepNotifications,
        // The notification center shows the safety representative's notifications with the rest
        element: <Navigate to={Routes.Notifications} replace />,
      },
      {
        path: Routes.SafetyRepSurvey,
        element: <SafetyRepArea><WorkEnvironmentSurveyDetailPage /></SafetyRepArea>,
      },
      {
        path: Routes.SafetyRepSigning,
        element: <SafetyRepArea><SafetyRepTasks /></SafetyRepArea>,
      },
      {
        path: Routes.SafetyRepCompleted,
        element: <SafetyRepArea><SafetyRepCompleted /></SafetyRepArea>,
      },
      {
        path: Routes.SafetyRepRiskMatrixVersion,
        element: <SafetyRepArea><SafetyRepVersion subject="riskMatrix" /></SafetyRepArea>,
      },
      {
        path: Routes.SafetyRepTemplateVersion,
        element: <SafetyRepArea><SafetyRepVersion subject="template" /></SafetyRepArea>,
      },
      // The team leader's routes
      {
        path: Routes.Dashboard,
        element: teamLeaderOnly(<DashboardPage />),
        showTeamSelector: true,
        teamSelectorInPage: true,
        children: [
          {
            path: Routes.DashboardPsychologicalSafety,
            element: <TeamDashboard />,
            showTeamSelector: true,
            teamSelectorInPage: true,
          },
          {
            path: Routes.DashboardWorkEnvironment,
            element: <WorkEnvironmentDashboardPage />,
            showTeamSelector: true,
            teamSelectorInPage: true,
          },
          {
            path: Routes.DashboardWorkEnvironmentSurvey,
            element: <WorkEnvironmentSurveyDetailPage />,
            showTeamSelector: false,
          },
          {
            path: Routes.DashboardCustomSurveys,
            element: <CustomSurveyDashboardPage />,
            showTeamSelector: true,
            teamSelectorInPage: true,
          },
          {
            path: Routes.DashboardCustomSurveyTemplateTrends,
            element: <CustomSurveyTemplateTrendsPage />,
            showTeamSelector: true,
            teamSelectorInPage: true,
            breadcrumbsInPage: true,
          },
          {
            path: Routes.DashboardCustomSurveySurvey,
            element: <CustomSurveyResultsPage />,
            showTeamSelector: true,
            teamSelectorInPage: true,
            breadcrumbsInPage: true,
          },
          {
            path: Routes.DashboardCustomSurveyResponseDetail,
            element: <CustomSurveyResultsPage />,
            showTeamSelector: true,
            teamSelectorInPage: true,
            breadcrumbsInPage: true,
          },
        ],
      },
      {
        path: Routes.Teams,
        element: teamLeaderOnly(<Teams />),
      },
      {
        path: Routes.CreateTeam,
        element: <CreateTeamPage />,
        focusMode: true,
      },
      {
        path: Routes.Team,
        element: teamLeaderOnly(<Team />),
      },
      {
        path: Routes.TeamAccessManagement,
        element: teamLeaderOnly(<TeamAccessManagement />),
      },
      {
        path: Routes.TeamIntegration,
        element: teamLeaderOnly(<TeamIntegration />),
      },
      {
        path: Routes.TeamScheduling,
        element: teamLeaderOnly(<TeamSchedulingPage />),
      },
      {
        path: Routes.QuestionTrends,
        element: teamLeaderOnly(<QuestionTrends />),
        showTeamSelector: true,
        teamSelectorInPage: true,
      },
      {
        path: Routes.CreateSurvey,
        element: teamLeaderOnly(<CreateSurveyPage />),
        focusMode: true,
      },
      {
        path: Routes.EditPlannedSurvey,
        element: teamLeaderOnly(<EditPlannedSurveyPage />),
        focusMode: true,
      },
      {
        path: Routes.Surveys,
        element: <Navigate to={Routes.CurrentSurveys} replace />,
      },
      {
        path: Routes.CurrentSurveys,
        element: teamLeaderOnly(<SurveysPage />),
      },
      {
        path: Routes.PlannedSurveys,
        element: teamLeaderOnly(<SurveysPage />),
      },
      {
        path: Routes.AnalyzeSurvey,
        element: teamLeaderOnly(<AnalyzeSurvey />),
        focusMode: true,
      },
      {
        path: Routes.AnalyzeSurveySummary,
        element: teamLeaderOnly(<AnalyzationSummary />),
        focusMode: true,
      },
      {
        path: Routes.InterpretiveReport,
        element: teamLeaderOnly(<InterpretiveReportPage />),
        focusMode: true,
      },
      {
        path: Routes.WorkEnvironmentReport,
        element: teamLeaderOnly(<WorkEnvironmentReportPage />),
        breadcrumbsInPage: true,
      },
      {
        path: Routes.HRWorkEnvironmentReport,
        element: hrOnly(<WorkEnvironmentReportPage />),
        breadcrumbsInPage: true,
      },
      {
        path: Routes.SafetyRepReport,
        element: <SafetyRepArea><WorkEnvironmentReportPage /></SafetyRepArea>,
        breadcrumbsInPage: true,
      },
      {
        path: Routes.WorkEnvironmentInspectionReport,
        element: teamLeaderOnly(<WorkEnvironmentInspectionReportPage />),
        focusMode: true,
      },
      {
        path: Routes.HRWorkEnvironmentInspectionReport,
        element: hrOnly(<WorkEnvironmentInspectionReportPage />),
        focusMode: true,
      },
      {
        path: Routes.SafetyRepInspectionReport,
        element: <SafetyRepArea><WorkEnvironmentInspectionReportPage /></SafetyRepArea>,
        focusMode: true,
      },
      {
        path: Routes.Settings,
        element: <Navigate to={Routes.UserSettings} replace />,
      },
      {
        path: Routes.TeamsOAuthCallback,
        element: <TeamsOAuthCallbackPage />,
        focusMode: true,
      },
      {
        path: Routes.SlackOAuthCallback,
        element: <SlackOAuthCallbackPage />,
        focusMode: true,
      },
      {
        path: Routes.ContentManagementSystemArticles,
        element: <ContentSystemPageLayout />,
        hideRoute: !window.localStorage.getItem('shouldShowCms'),
        children: [
          {
            index: true,
            element: <ContentSystemArticlesPage />,
          },
          {
            path: 'create',
            element: <ContentSystemPage mode="create" />,
          },
          {
            path: 'edit/:id',
            element: <ContentSystemPage mode="edit" />,
          },
        ],
      },
      {
        path: Routes.ContentManagementSystemArticle,
        element: <ArticlePage />,
      },
      {
        path: Routes.UserSettings,
        element: <SettingsPage />,
      },
      {
        path: Routes.UserPrivacy,
        element: <SettingsPage />,
      },
      {
        path: Routes.CustomSurveyTemplates,
        element: <CustomSurveyTemplatesPage />,
      },
      {
        path: Routes.CreateCustomSurveyTemplate,
        element: <CreateCustomSurveyTemplatePage />,
        breadcrumbsInPage: true,
      },
      {
        path: Routes.CustomSurveyTemplateDetail,
        element: <CustomSurveyTemplateDetailPage />,
        breadcrumbsInPage: true,
      },
      {
        path: Routes.EditCustomSurveyTemplate,
        element: <CustomSurveyTemplateEditPage />,
        breadcrumbsInPage: true,
      },
      {
        path: Routes.OrganizationTeamsOverview,
        element: orgAdminOnly(<TeamsOverviewPage />),
      },
      {
        path: Routes.OrganizationUsage,
        element: orgAdminOnly(<OrganizationUsagePage />),
      },
      {
        path: Routes.OrganizationUsageTeamDetails,
        element: orgAdminOnly(<TeamUsageDetailsPage />),
        breadcrumbsInPage: true,
      },
      {
        path: Routes.OrganizationCreateTeam,
        element: <CreateTeamPage />,
        focusMode: true,
      },
      {
        path: Routes.OrganizationUsersOverviewPage,
        element: orgAdminOnly(<UsersOverviewPage />),
      },
      {
        path: Routes.OrganizationInviteUser,
        element: orgAdminOnly(<InviteUserPage />),
        breadcrumbsInPage: true,
      },
      {
        path: Routes.OrganizationViewUser,
        element: orgAdminOnly(<ViewUserPage />),
        breadcrumbsInPage: true,
      },
      {
        path: Routes.OrganizationEditUser,
        element: orgAdminOnly(<EditUserPage />),
        breadcrumbsInPage: true,
      },
      {
        path: Routes.OrganizationTeam,
        element: <Team />,
      },
      {
        path: Routes.OrganizationTeamScheduling,
        element: <TeamSchedulingPage />,
      },
      {
        path: Routes.OrganizationSettings,
        element: orgAdminOnly(<OrganizationSettingsPage />),
      },
      {
        path: Routes.OrganizationBusiness,
        element: orgAdminOnly(<OrganizationBusinessPage />),
      },
      {
        path: Routes.OrganizationScheduling,
        element: <OrganizationSchedulingPage />,
      },
      {
        path: Routes.OrganizationGeneral,
        element: <OrganizationGeneralPage />,
      },
      {
        path: Routes.OrganizationSubscriptions,
        element: orgAdminOnly(<SubscriptionsPage />),
      },
      // An address that matches no page, also under a part such as /hr/dashboard
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ].filter((route: ExtendedRouteObject) => {
      return !route.hideRoute;
    }),
  },
  // System Admin routes (separate layout)
  {
    path: Routes.SystemAdmin,
    element: <SystemAdminLayout />,
    children: [
      {
        index: true,
        element: <Navigate to={Routes.SystemAdminDashboard} replace />,
      },
      {
        path: Routes.SystemAdminDashboard,
        element: <SystemAdminDashboard />,
      },
      {
        path: Routes.SystemAdminTenants,
        element: <TenantOverviewPage />,
      },
      {
        path: Routes.SystemAdminCreateTenant,
        element: <CreateTenantPage />,
        focusMode: true,
      },
      {
        path: Routes.SystemAdminTenantDetail,
        element: <TenantDetailPageNew />,
      },
      {
        path: Routes.SystemAdminTenantFeatures,
        element: <TenantFeaturesPage />,
      },
      {
        path: Routes.SystemAdminEditSmsQuota,
        element: <EditSmsQuotaPage />,
        focusMode: true,
      },
      {
        path: Routes.SystemAdminEditSeats,
        element: <EditSeatsPage />,
        focusMode: true,
      },
      {
        path: Routes.SystemAdminUpgradeTenantToPartner,
        element: <UpgradeTenantToPartnerPage />,
        focusMode: true,
      },
      {
        path: Routes.SystemAdminPartners,
        element: <PartnerOverviewPage />,
      },
      {
        path: Routes.SystemAdminCreatePartner,
        element: <CreatePartnerPage />,
        focusMode: true,
      },
      {
        path: Routes.SystemAdminPartnerDetail,
        element: <PartnerDetailPage />,
      },
      {
        path: Routes.SystemAdminEditPartner,
        element: <EditPartnerPage />,
        focusMode: true,
      },
      {
        path: Routes.SystemAdminPartnerLeadsOverview,
        element: <PartnerLeadsOverviewPage />,
      },
      {
        path: Routes.SystemAdminUserDetail,
        element: <UserDetailPage />,
      },
    ],
  },
  // Partner Admin routes (separate layout)
  {
    path: Routes.PartnerAdmin,
    element: <PartnerAdminLayout />,
    children: [
      {
        index: true,
        element: <Navigate to={Routes.PartnerAdminDashboard} replace />,
      },
      {
        path: Routes.PartnerAdminDashboard,
        element: <PartnerAdminDashboard />,
      },
      {
        path: Routes.PartnerAdminCustomers,
        element: <CustomerListPage />,
      },
      {
        path: Routes.PartnerAdminCustomerDetail,
        element: <CustomerDetailPage />,
      },
      {
        path: Routes.PartnerAdminCreateCustomer,
        element: <CreateCustomerPage />,
        focusMode: true,
      },
      {
        path: Routes.PartnerAdminLeads,
        element: <LeadListPage />,
      },
      {
        path: Routes.PartnerAdminCreateLead,
        element: <CreateLeadPage />,
        focusMode: true,
      },
      {
        path: Routes.PartnerAdminLeadDetail,
        element: <LeadDetailPage />,
      },
      {
        path: Routes.PartnerAdminEditLead,
        element: <EditLeadPage />,
        focusMode: true,
      },
      {
        path: Routes.PartnerAdminCashback,
        element: <CashbackPage />,
      },
    ],
  },
];

/**
 * Generic function to collect routes matching a specific property
 */
function collectRoutes(
  propertyCheck: (route: ExtendedRouteObject) => boolean,
  buildFullPath: boolean = false,
): string[] {
  const routes: string[] = [];

  // Helper to process routes recursively
  const processRoute = (route: ExtendedRouteObject, parentPath: string = '') => {
    if (propertyCheck(route) && route.path) {
      let fullPath: string;

      if (buildFullPath) {
        // If the route path is absolute (starts with /), use it as-is
        // Otherwise, concatenate with parent path
        if (route.path.startsWith('/')) {
          fullPath = route.path;
        } else if (parentPath) {
          fullPath = `${parentPath}/${route.path}`;
        } else {
          fullPath = route.path;
        }
      } else {
        fullPath = route.path;
      }

      routes.push(fullPath);
    }

    if (route.children) {
      const currentPath = route.path || parentPath;
      route.children.forEach((childRoute: ExtendedRouteObject) => {
        processRoute(childRoute, currentPath);
      });
    }
  };

  // Process all routes, not just loggedInRoutes
  routesWithOptions.forEach((route) => {
    processRoute(route);
  });

  return routes;
}

function getTeamSelectorVisibility(location: ReturnType<typeof useLocation>) {
  const routes = collectRoutes((route) => !!route.showTeamSelector);
  return matchRoute(location.pathname, routes);
}

function getTeamSelectorInPage(location: ReturnType<typeof useLocation>) {
  const routes = collectRoutes((route) => !!route.teamSelectorInPage);
  return matchRoute(location.pathname, routes);
}

function getShouldBeFocusMode(location: ReturnType<typeof useLocation>) {
  const routes = collectRoutes((route) => !!route.focusMode, true);
  return matchRoute(location.pathname, routes);
}

function createPattern(route: string): RegExp {
  return new RegExp('^' + route.replace(/:([^/]+)/g, '([^/]+)') + '$');
}

function matchRoute(path: string, templates: string[]): boolean {
  return templates.some((template) => {
    const pattern = createPattern(template);
    return pattern.test(path);
  });
}
